/**
 * Tuning helpers: derive a TuningContext from Settings, label degrees, and compute the fire-ring scale mask.
 */
import {
  FIFTHS_ORDER, PITCH_CLASS_NAMES, SA_OCTAVE0_MIDI_C, makeTuning, sargamName, scaleById, saHz,
  type PitchSpec, type Scale, type Settings, type TuningContext,
} from '@tonist/shared';

export const tuningFromSettings = (s: Pick<Settings, 'tonicPc' | 'scaleId' | 'ji'>): TuningContext =>
  makeTuning(s.tonicPc, s.scaleId, s.ji);

/** Fire-ring mask in FIFTHS_ORDER sector order: is that pitch class in the current scale. */
export function scaleMask(scale: Scale, tonicPc: number): boolean[] {
  const inScale = new Set(scale.semitones.map((st) => (st + tonicPc) % 12));
  return FIFTHS_ORDER.map((pc) => inScale.has(pc));
}

/** Semitone offset from Sa for a degree (degree N wraps to 12). */
export function degreeSemitone(scale: Scale, degree: number): number {
  const n = scale.semitones.length;
  const wrapped = ((degree % n) + n) % n;
  return scale.semitones[wrapped]! + 12 * Math.floor(degree / n);
}

/** Scientific pitch name for a MIDI note, e.g. 65 → "F4". */
export function midiName(midi: number): string {
  const pc = ((Math.round(midi) % 12) + 12) % 12;
  return `${PITCH_CLASS_NAMES[pc]}${Math.floor(Math.round(midi) / 12) - 1}`;
}

export interface DegreeLabel { sargam: string; letter: string; midi: number }

/** Labels for a zone: sargam ("Ga") and letter with octave ("F4"). */
export function degreeLabel(scale: Scale, tonicPc: number, degree: number, octave: number): DegreeLabel {
  const st = degreeSemitone(scale, degree);
  const midi = SA_OCTAVE0_MIDI_C + tonicPc + 12 * octave + st;
  return { sargam: sargamName(st % 12 === 0 && st > 0 ? 12 : st % 12), letter: midiName(midi), midi };
}

export const pitchLabel = (scale: Scale, tonicPc: number, p: PitchSpec): DegreeLabel => degreeLabel(scale, tonicPc, p.degree, p.octave);

/** "Ga, F4" for aria-labels. */
export const zoneAriaLabel = (scale: Scale, tonicPc: number, degree: number, octave: number): string => {
  const l = degreeLabel(scale, tonicPc, degree, octave);
  return `${l.sargam}, ${l.letter}`;
};

export const tonicName = (tonicPc: number): string => PITCH_CLASS_NAMES[((tonicPc % 12) + 12) % 12]!;

/** Step the tonic around the circle of fifths (dir +1 = up a fifth). */
export const stepTonicFifths = (tonicPc: number, dir: number): number => (((tonicPc + 7 * Math.sign(dir)) % 12) + 12) % 12;
export const stepTonicSemitones = (tonicPc: number, dir: number): number => (((tonicPc + Math.sign(dir)) % 12) + 12) % 12;

/** Index of the anchor degree (Pa / Ma / Ni) inside the scale, or -1. */
export function anchorDegree(scale: Scale): number {
  const target = scale.anchor === 'Pa' ? 7 : scale.anchor === 'Ma' ? 5 : 11;
  const i = scale.semitones.indexOf(target);
  if (i >= 0) return i;
  if (scale.anchor === 'Ma') return scale.semitones.indexOf(6);
  return -1;
}

export { scaleById, saHz };
